import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, message } from 'antd';
import type { SelectProps } from 'antd';
import { useModel } from '@umijs/max';
import { getStoredDistrictCode } from '@/utils/tools';
import { getLocationOfficeList } from './api';

type OfficeRecord = {
  officeCode: number | string;
  officeName: string;
};

type OfficeSelectOption = {
  label: string;
  value: number;
};

type OfficeSelectProps = Omit<SelectProps, 'options' | 'loading'> & {
  /** 区县 code，不传则使用当前已选区县 */
  districtCode?: number | string;
  /** 是否多选，默认单选；也可通过 mode 直接控制 */
  multiple?: boolean;
};

const toNumberCode = (value: unknown): number | undefined => {
  const code = Number(value);
  return Number.isNaN(code) || !code ? undefined : code;
};

const parseOfficeList = (data: unknown): OfficeRecord[] => {
  if (Array.isArray(data)) {
    return data as OfficeRecord[];
  }
  if (data && typeof data === 'object' && Array.isArray((data as { list?: unknown }).list)) {
    return (data as { list: OfficeRecord[] }).list;
  }
  return [];
};

const OfficeSelect: React.FC<OfficeSelectProps> = ({
  districtCode: districtCodeProp,
  placeholder,
  allowClear = true,
  disabled,
  multiple = false,
  mode,
  ...selectProps
}) => {
  const { initialState } = useModel('@@initialState');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<OfficeSelectOption[]>([]);

  const districtCode = useMemo(() => {
    const explicit = toNumberCode(districtCodeProp);
    if (explicit != null) {
      return explicit;
    }
    const fromState = toNumberCode(initialState?.selectedDistrictCode);
    if (fromState != null) {
      return fromState;
    }
    return getStoredDistrictCode();
  }, [districtCodeProp, initialState?.selectedDistrictCode]);

  const loadOptions = useCallback(async () => {
    if (districtCode == null) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const res: any = await getLocationOfficeList({ districtCode });
      if (res?.code === 200) {
        const list = parseOfficeList(res.data);
        setOptions(
          list
            .map((item) => ({
              label: item.officeName,
              value: Number(item.officeCode),
            }))
            .filter((item) => !Number.isNaN(item.value) && item.label),
        );
      } else {
        setOptions([]);
        message.error(res?.msg || '获取街道办事处列表失败');
      }
    } catch {
      setOptions([]);
      message.error('获取街道办事处列表失败');
    } finally {
      setLoading(false);
    }
  }, [districtCode]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const resolvedPlaceholder =
    placeholder ?? (districtCode ? '请选择街道办事处' : '请先选择区县');
  const resolvedMode = mode ?? (multiple ? 'multiple' : undefined);

  return (
    <Select
      placeholder={resolvedPlaceholder}
      allowClear={allowClear}
      disabled={disabled ?? !districtCode}
      loading={loading}
      options={options}
      {...selectProps}
      mode={resolvedMode}
    />
  );
};

export default OfficeSelect;
